import { format as formatDate } from "date-fns";
import { DatePicker as RSDatePicker } from "rsuite";
import ValidationErrorMessage from "./ValidationErrorMessage";
import "rsuite/dist/rsuite.min.css";
import {
  FocusEvent,
  KeyboardEvent,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import IsRequired from "./common/IsRequired";
import { splitPickerChange, toPickerDate } from "./datePickerValue";

// rsuite rebuilds its month/year list every time the DatePicker re-renders, and a
// click that lands on a rebuilt cell is lost (mousedown and mouseup hit different
// DOM nodes). Formik re-renders on every blur, so without this the first click on
// a month did nothing. Keep the props below referentially stable.
const StableRSDatePicker = memo(RSDatePicker);

const preventEnterSubmit = (e: KeyboardEvent) => {
  if (e.key === "Enter") {
    e.preventDefault();
    return false;
  }
};

export const rsDatePickerLocale = {
  hours: "Timer",
  minutes: "Minutter",
  monday: "Man",
  tuesday: "Tir",
  wednesday: "Ons",
  thursday: "Tors",
  friday: "Fre",
  saturday: "Lør",
  sunday: "Søn",
};
export function FormikDatePicker<U>(props: FormikDateControlProps<U>) {
  // Turn off autocomplete for input element within RSDatePicker
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) {
      const target = (ref.current as any).target as HTMLDivElement;
      const inputs = target.getElementsByTagName("input");
      if (inputs.length > 0) {
        const input = inputs[0] as HTMLInputElement;
        input.autocomplete = "off";
      }
    }
  }, [ref]);

  const { config, formik, disabled, placeholder } = props;
  const { id, label, initialValue, isRequired } = config;
  const { values, handleBlur, setFieldValue, setFieldTouched } = formik;
  // An unfinished date, kept on screen while Formik holds null (ADV-320).
  const [draft, setDraft] = useState<Date | null>(null);
  const dayTime = toPickerDate(values[id])?.getTime() ?? null;
  const pickerValue = useMemo(
    () => (dayTime === null ? draft : new Date(dayTime)),
    [dayTime, draft],
  );
  const ranges = useMemo(
    () => [{ label: "I dag", value: new Date(), placement: "bottom" as const }],
    [],
  );

  const handleChange = useCallback(
    (date: Date | null) => {
      const next = splitPickerChange(date);
      setDraft(next.draft);
      setFieldValue(id, next.value, true);
    },
    [id, setFieldValue],
  );

  // Focus moves between the input and the popup while it is open; that is not the
  // user leaving the field. Marking it touched then would re-render mid-click.
  const popupOpen = useRef(false);
  const handleOpen = useCallback(() => {
    popupOpen.current = true;
  }, []);
  const handleClose = useCallback(() => {
    popupOpen.current = false;
    setFieldTouched(id, true);
  }, [id, setFieldTouched]);
  const handleBlurCapture = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (!popupOpen.current && e.target.id === id) handleBlur(e);
    },
    [id, handleBlur],
  );

  return (
    <>
      {label && (
        <label style={{ display: "block" }} className="form-label" htmlFor={id}>
          {label}
          {isRequired && <IsRequired />}
        </label>
      )}
      <StableRSDatePicker
        isoWeek
        ref={ref}
        aria-autocomplete="none"
        onKeyDown={preventEnterSubmit}
        id={id}
        limitEndYear={100}
        oneTap
        className={`flex-100 d-block w-100${
          formik.touched[id] && (formik as any).errors[id] ? " is-invalid" : ""
        }`}
        placeholder={
          placeholder
            ? placeholder
            : initialValue
              ? formatDate(new Date(initialValue), "dd.MM.yyyy")
              : "dd.mm.åååå"
        }
        format={"dd.MM.yyyy"}
        disabled={disabled}
        value={pickerValue}
        onBlurCapture={handleBlurCapture}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        ranges={ranges}
        locale={rsDatePickerLocale}
        placement="autoVerticalStart"
      />

      <ValidationErrorMessage
        touched={formik.touched[id]}
        errorMessage={(formik as any).errors[id]}
      />
    </>
  );
}
